/**
 * Builds the user-role message for one turn.
 *
 * The heavy lifting (action types, JSON schemas, rules) lives in the
 * LibreChat Agent's "Instructions" field — configured once in the Agent
 * Builder UI via agent-instructions.md.
 *
 * A turn carries only the dynamic, per-request bits:
 *   - Excel environment (locale settings)
 *   - Current spreadsheet data
 *   - The user's message
 *
 * The workbook context is optional. When present, the turn is rendered twice
 * — once with the real snapshot and once with a placeholder in its place — so
 * `pruneSnapshots` can swap in the snapshot-free variant later without having
 * to find and cut the data block out of arbitrary prompt text.
 *
 * The template is read from the ADDON_CONTEXT_PROMPT env var (set in .env).
 * Placeholders: {{displayLanguage}}, {{decimalSeparator}}, {{listSeparator}},
 *               {{spreadsheetData}}, {{userMessage}}
 */

import {ActionResult, OUTCOME_MARKERS} from "./actionExecutor";
import {SNAPSHOT_PLACEHOLDER, TurnMessage} from "./conversationHistory";
import {ExcelSettings} from "./excelContext";

export interface WorkbookContext {
  settings?: ExcelSettings;
  spreadsheetData: string;
}

const DEFAULT_TEMPLATE = `## Excel Environment
- Display language: {{displayLanguage}}
- Decimal separator: "{{decimalSeparator}}"
- Formula argument separator: "{{listSeparator}}"

## Spreadsheet Data (CSV)
\`\`\`csv
{{spreadsheetData}}
\`\`\`

## User Request
{{userMessage}}`;

export function buildUserTurn(
  userMessage: string,
  context?: WorkbookContext,
): TurnMessage {
  if (!context) {
    return {role: "user", content: userMessage};
  }

  const {settings, spreadsheetData} = context;
  const template =
    (process.env.ADDON_CONTEXT_PROMPT || "").trim() || DEFAULT_TEMPLATE;

  const render = (data: string) =>
    fillEnvironment(template, settings)
      .replace(/\{\{spreadsheetData\}\}/g, data)
      .replace(/\{\{userMessage\}\}/g, userMessage);

  return {
    role: "user",
    content: render(spreadsheetData || "(no data available)"),
    prunedContent: render(SNAPSHOT_PLACEHOLDER),
  };
}

/**
 * The result markers explained to the model, built from `OUTCOME_MARKERS` so
 * the legend can't drift from what `buildFollowUpTurn` actually writes.
 */
const OUTCOME_LEGEND =
  `${OUTCOME_MARKERS.success} (done), ` +
  `${OUTCOME_MARKERS.failure} (failed) or ` +
  `${OUTCOME_MARKERS.declined} (the user declined it at a confirmation prompt)`;

const FOLLOW_UP_TEMPLATE = `## Excel Environment
- Display language: {{displayLanguage}}
- Decimal separator: "{{decimalSeparator}}"
- Formula argument separator: "{{listSeparator}}"

## Spreadsheet Data (CSV)
\`\`\`csv
{{spreadsheetData}}
\`\`\`

## Previous Action Results
{{actionResults}}

## Instructions
Review the results above against the refreshed spreadsheet data. Each result is marked ${OUTCOME_LEGEND}. A ${OUTCOME_MARKERS.declined} result is the user's decision and is settled — do not retry it and do not propose an equivalent script; carry on without it, or say plainly that it was skipped. If the task is now fully done, reply in plain text with no action blocks. Otherwise, correct what failed or continue with the next step by emitting the necessary action block(s).`;

/**
 * Builds the follow-up user message for a loop iteration: the outcome of the
 * actions just run, plus a freshly read view of the workbook so the model
 * can tell a working formula from a broken one.
 */
export function buildFollowUpTurn(
  actionResults: ActionResult[],
  context: WorkbookContext,
): TurnMessage {
  const {settings, spreadsheetData} = context;

  const resultsText =
    actionResults
      .map((r) => {
        const line = `${OUTCOME_MARKERS[r.outcome]} ${r.message}`;
        return r.detail ? `${line}\n${r.detail}` : line;
      })
      .join("\n") || "(no actions were emitted)";

  const render = (data: string) =>
    fillEnvironment(FOLLOW_UP_TEMPLATE, settings)
      .replace(/\{\{spreadsheetData\}\}/g, data)
      .replace(/\{\{actionResults\}\}/g, resultsText);

  return {
    role: "user",
    content: render(spreadsheetData || "(no data available)"),
    prunedContent: render(SNAPSHOT_PLACEHOLDER),
  };
}

function fillEnvironment(template: string, settings?: ExcelSettings): string {
  return template
    .replace(/\{\{displayLanguage\}\}/g, settings?.displayLanguage ?? "en-US")
    .replace(/\{\{decimalSeparator\}\}/g, settings?.decimalSeparator ?? ".")
    .replace(/\{\{listSeparator\}\}/g, settings?.listSeparator ?? ",");
}
